import React, { useEffect, useState } from "react";
import {
  FaCheckCircle,
  FaExclamationCircle,
  FaFolderOpen,
  FaHourglassHalf,
  FaLock,
  FaLockOpen,
  FaThumbsUp,
  FaUser,
} from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Input,
  Label,
  Row,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
  fetchByGaurd,
  fetchByRegion,
  fetchDashboardDetails,
} from "../../store/dashboard/actions";
import GuardWiseTicketsTable from "./gaurdTicketwise";
import IncidentChart from "./incidentChart";
import StatusChart from "./statusChart";
import { computeDateRange, getEndOfDay, getTimestamp } from "../../helpers/formatDate";
import OneSignal from "react-onesignal";
import { CommonDatePicker } from "../../components/Common/Datepicker/CommonDatePicker";
import { CommonMyDatePicker } from "../../components/Common/Datepicker/CommonMyDatePicker";

const dashboardItems = [
  {
    title: "All",
    icon: <FaExclamationCircle size={32} color="#0056b3" />,
    color: "#8fcfff",
  },
  {
    title: "Open",
    icon: <FaFolderOpen size={32} color="#e65100" />,
    color: "#ffe0b2",
  },
  {
    title: "Assigned",
    icon: <FaUser size={32} color="#2e7d32" />,
    color: "#c8e6c9",
  },
  {
    title: "Acknowledged",
    icon: <FaThumbsUp size={32} color="#6a1b9a" />,
    color: "#f0cbf5",
  },
  {
    title: "Working",
    icon: <FaHourglassHalf size={32} color="#004d40" />,
    color: "#b2dfdb",
  },
  {
    title: "Resolved",
    icon: <FaCheckCircle size={32} color="#ff8f00" />,
    color: "#ffecb3",
  },
  {
    title: "Closed",
    icon: <FaLock size={32} color="#b71c1c" />,
    color: "#ffcdd2",
  },
  {
    title: "Reopen",
    icon: <FaLockOpen size={32} color="#b71c1c" />,
    color: "#cdcdd2",
  },
];

const Dashboard = () => {
  const [searchRegion, setSearchRegion] = useState("");
  const [searchIncidentType, setSearchIncidentType] = useState("");
  const [uniqueIncidentTypes, setUniqueIncidentTypes] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [debouncedSearchRegion, setDebouncedSearchRegion] = useState("");
  
  const dispatch = useDispatch();
  const { data, regionData, error } = useSelector(
    (state) => state.dashboardReducer
  );

 
  // Initialize dates on component mount (last 30 days)
  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    setFromDate(getTimestamp(thirtyDaysAgo));
    setToDate(getTimestamp(today));
  }, []);

  useEffect(() => {
    const channel = new BroadcastChannel("dashboardChannel");
    channel.onmessage = (event) => {
      if (event.data === "update_dashboard" || event.data === "update_dashboards") {
        dispatch(fetchDashboardDetails());
        if (fromDate && toDate) {
          dispatch(fetchByRegion("", getTimestamp(fromDate), getTimestamp(toDate)));
        }
      }
    };

    return () => {
      channel.close();
    };
  }, [dispatch, fromDate, toDate]);

  const userinfo = localStorage.getItem("authUser")
    ? JSON.parse(localStorage.getItem("authUser"))
    : "";

  useEffect(() => {
    const initialize = async () => {
      try {
        dispatch(fetchDashboardDetails());
      } catch (error) {
        console.error("OneSignal init/login error:", error);
      }
    };
    initialize();

    const incidents = JSON.parse(
      localStorage.getItem("incidentTypes") || "[]"
    );
    setUniqueIncidentTypes(incidents);
  }, [dispatch]);


  // Updated OneSignal implementation with service worker handling
  useEffect(() => {
    const initOneSignal = async () => {
      try {
        console.log("✅ OneSignal initialized successfully");
        await OneSignal.setConsentGiven(true);
        console.log("✅ Consent provided");
        try {
          await OneSignal.login(userinfo.userId);
          const extId = OneSignal.User.externalId;
          const onesignalId = OneSignal.User.onesignalId;
          console.log("✅ External ID set:", extId, onesignalId);
        } catch (loginErr) {
          console.error("⚠️ Error setting external ID:", loginErr);
          console.log("⚠️ Continuing without external ID");
        }

        const notificationPermission = await OneSignal.Notifications.permission;
        console.log(
          "🔔 Current notification permission:",
          notificationPermission
        );

        if (notificationPermission !== "granted") {
          console.log("🔔 Showing notification permission prompt...");
          try {
            const result = await OneSignal.Notifications.requestPermission();
            console.log("🔔 Permission request result:", result);
          } catch (permErr) {
            console.error("⚠️ Permission request error:", permErr);
          }
        }

        const isSubscribed = await OneSignal.User.PushSubscription.optedIn;
        console.log("🔔 Is user actually subscribed?", isSubscribed);

        if (!isSubscribed && notificationPermission === "granted") {
          console.log(
            "⚠️ User has permission but not subscribed - attempting to fix..."
          );
          await OneSignal.User.PushSubscription.optIn();
          console.log("🔄 Manually opted user in");
        }

        try {
          await OneSignal.User.addTag("user_id", userinfo.userId);
          await OneSignal.User.addTag("last_login", new Date().toISOString());
          console.log("✅ User tags added");
        } catch (tagErr) {
          console.error("⚠️ Error adding user tags:", tagErr);
        }

        OneSignal.Notifications.addEventListener(
          "foregroundWillDisplay",
          handleNotificationReceived
        );
      } catch (err) {
        console.error("❌ OneSignal setup failed:", err);
        console.error("Error details:", {
          name: err.name,
          message: err.message,
          stack: err.stack,
        });

        if (err.message && err.message.includes("MIME type")) {
          console.error(
            "⚠️ Service worker MIME type issue detected. Please check your server configuration."
          );
        }
      }
    };

    const handleNotificationReceived = async (event) => {
      try {
        console.log("🔔 Notification received in foreground:", event);
        dispatch(fetchDashboardDetails());
        if (fromDate && toDate) {
          dispatch(fetchByRegion("", getTimestamp(fromDate), getTimestamp(toDate)));
        }
        dispatch(fetchByGaurd());
      } catch (error) {
        console.error("❌ Error handling notification received:", error);
      }
    };

    initOneSignal();

    return () => {
      OneSignal.Notifications.removeEventListener(
        "foregroundWillDisplay",
        handleNotificationReceived
      );
    };
  }, [fromDate, toDate]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchRegion(searchRegion);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchRegion]);

  // Fetch regions when debounced value updates or dates change
  useEffect(() => {
    if (fromDate && toDate) {
      dispatch(fetchByRegion(
        debouncedSearchRegion, 
        getTimestamp(fromDate), 
        getTimestamp(toDate), 
        searchIncidentType
      ));
    }
  }, [debouncedSearchRegion, fromDate, toDate, searchIncidentType, dispatch]);

  const handleIncidentChange = (incidentTypeId) => {
    setSearchIncidentType(incidentTypeId);
  };

  const handleFromDateChange = (date) => {
    setFromDate(date);
  };

  const handleToDateChange = (date) => {
    setToDate(getEndOfDay(date));
  };

  const ticketCounts = data?.count || {};
  const today = new Date();

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Control Room" breadcrumbItem="Dashboard" />

        <Row style={{ gap: "15px", marginTop: "15px" }}>
          {error ? (
            <Col lg={12}>
              <Card className="shadow-sm">
                <CardBody>
                  <h5 className="text-danger">Error: {error}</h5>
                </CardBody>
              </Card>
            </Col>
          ) : (
            dashboardItems.map((item) => (
              <Col lg={2} md={6} sm={12} key={item.title}>
                <Card
                  style={{
                    background: item.color,
                    borderRadius: "12px",
                    border: "none",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.transform = "translateY(-8px)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.transform = "translateY(0)")
                  }
                >
                  <CardBody
                    className="text-center py-4"
                    style={{ textAlign: "center" }}
                  >
                    <div style={{ fontSize: "22px", marginBottom: "8px" }}>
                      {item.icon}
                    </div>
                    <CardTitle
                      tag="h6"
                      style={{
                        fontWeight: "500",
                        fontSize: "16px",
                        marginBottom: "5px",
                        color: "#212121",
                      }}
                    >
                      {item.title}
                    </CardTitle>
                    <h4
                      style={{
                        fontWeight: "bold",
                        fontSize: "28px",
                        marginTop: "5px",
                        color: "#212121",
                      }}
                    >
                      {ticketCounts[item.title] || 0}
                    </h4>
                  </CardBody>
                </Card>
              </Col>
            ))
          )}
        </Row>
        <>
          {ticketCounts && regionData && (
            <Card>
              <CardBody>
                <Row style={{ marginBottom: "40px" }}>
                  <Col md={2}>
                    <CommonDatePicker
                      placeholderText="Start Date"
                      label="Start Date"
                      value={fromDate}
                      onChange={handleFromDateChange}
                      disabled={false}
                      maxDate={today}
                    />
                  </Col>
                  <Col md={2}>
                    <CommonMyDatePicker
                      placeholderText="End Date"
                      label="End Date"
                      value={toDate}
                      onChange={handleToDateChange}
                      maxDate={today}
                      minDate={fromDate}
                    />
                  </Col>
                  <Col md={3}>
                    <Label for="incidentTypeFilter">Incident Type</Label>
                    <Input
                      type="select"
                      value={searchIncidentType}
                      onChange={(e) => handleIncidentChange(e.target.value)}
                    >
                      <option value="">All Types</option>
                      {uniqueIncidentTypes?.map((incident) => (
                        <option
                          key={incident.id}
                          value={incident.incidentTypeId}
                        >
                          {incident.incidentName}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={3}>
                    <Label for="regionFilter">Search</Label>
                    <Input
                      type="text"
                      placeholder="Search by Beats"
                      value={searchRegion}
                      onChange={(e) => setSearchRegion(e.target.value)}
                    />
                  </Col>
                </Row>
                <IncidentChart
                  incidentCounts={regionData?.counts?.incidentTypeCounts || {}}
                />
                <Row>
                  <StatusChart
                    statusCounts={regionData?.counts?.statusCounts || {}}
                  />
                </Row>
              </CardBody>
            </Card>
          )}
          <GuardWiseTicketsTable />
        </>
      </Container>
    </div>
  );
};

export default Dashboard;